import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// Same-origin API base, keyed on PAGE PROTOCOL (the pattern set in App.tsx — the
// canonical rule every page, incl. future domain pages, inherits). NOT NODE_ENV: the
// CRA dev server is `development` even behind an HTTPS front (Tailscale Serve), so
// keying on env appended :5000 under HTTPS and pointed at nothing.
//   • HTTPS page → same-origin :443, no port (herexy nginx / Tailscale Serve path-mount).
//   • HTTP page  → Flask :5000 direct — the countersigned LAN/Electron path, unchanged.
const API_BASE = window.location.protocol === 'https:'
  ? `https://${window.location.host}/api`
  : `${window.location.protocol}//${window.location.hostname}:5000/api`;

interface TaskEntry {
  id: string;
  title: string;
  status: 'in_progress' | 'queued' | 'idea' | 'done';
  agent: 'sparticus' | 'ghost' | 'joint' | 'unassigned';
  domain?: string;
  note?: string;
  timestamp?: string;
  ghost_review?: 'pending' | 'flagged' | 'clear';
}

// Agent proposals — queued here, never applied until user confirms
interface TaskProposal {
  id: string;
  taskId: string | null;       // null = new task being proposed
  taskTitle?: string;
  proposedStatus: TaskEntry['status'];
  proposedAgent?: string;
  proposedDomain?: string;
  agent: string;               // who filed the proposal (normalised from proposedBy on fetch)
  jointSignoff?: { sparticus: boolean; ghost: boolean };
  message: string;             // Agent's explanation
  timestamp: string;
  type: 'status_change' | 'new_task' | 'mark_done';
}

interface CodePageProps {
  authToken: string | null;
  onBack: () => void;
  initialPreview?: { code: string; filename: string; lang: string } | null;
}

function AgentDot({ agent }: { agent: string }) {
  const color = agent === 'sparticus' ? '#7F77DD'
    : agent === 'ghost' ? '#1D9E75'
    : agent === 'joint' ? '#BA7517'
    : 'var(--color-text-tertiary)';
  return <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}

const SPARTICUS_WORKERS = new Set(['sparticus','forge','pixel','sherman','vault','pachy','dash']);
const GHOST_WORKERS     = new Set(['ghost','wire','piper','runner','atlas','gdog']);
const VALID_PROPOSAL_TYPES = new Set<string>(['new_task', 'mark_done', 'status_change']);

function agentSideStyle(w: string | undefined): React.CSSProperties {
  if (!w)                       return { background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' };
  if (w === 'joint')            return { background: '#FAEEDA', color: '#633806' };
  if (SPARTICUS_WORKERS.has(w)) return { background: '#EEEDFE', color: '#3C3489' };
  if (GHOST_WORKERS.has(w))     return { background: '#E1F5EE', color: '#085041' };
  return { background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' };
}

function AgentBadge({ agent }: { agent: string | undefined }) {
  return (
    <span style={{ ...agentSideStyle(agent), fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500 }}>
      {agent ? agent.charAt(0).toUpperCase() + agent.slice(1) : '?'}
    </span>
  );
}

function StatusIcon({ status }: { status: string }) {
  const map: Record<string, { icon: string; bg: string; color: string }> = {
    in_progress: { icon: '▶', bg: '#EEEDFE', color: '#3C3489' },
    queued:      { icon: '○', bg: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' },
    idea:        { icon: '✦', bg: '#E1F5EE', color: '#085041' },
    done:        { icon: '✓', bg: '#EAF3DE', color: '#27500A' },
  };
  const m = map[status] || map.queued;
  return (
    <div style={{
      width: 20, height: 20, borderRadius: '50%',
      background: m.bg, color: m.color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 500, flexShrink: 0, marginTop: 1,
    }}>
      {m.icon}
    </div>
  );
}

const DEMO_TASKS: TaskEntry[] = [
  { id: '1', title: 'Arthur trading dashboard — market state panel', status: 'in_progress', agent: 'sparticus', domain: 'Trading', ghost_review: 'pending', timestamp: '2026-05-01' },
  { id: '2', title: 'Memory manager page — full dashboard spec', status: 'in_progress', agent: 'joint', domain: 'Core infrastructure', timestamp: '2026-05-01' },
  { id: '3', title: 'VPS memory queue — per-domain sync files', status: 'queued', agent: 'unassigned', domain: 'Phase 13' },
  { id: '4', title: 'Ghost review pulse — SENTINEL-style scheduler', status: 'queued', agent: 'ghost', domain: 'After memory manager' },
  { id: '5', title: 'Santi chat fix — SecurityPage.tsx routing', status: 'queued', agent: 'sparticus', domain: 'Security' },
  { id: '6', title: 'conversations.json merge on local reconnect', status: 'queued', agent: 'unassigned', domain: 'Phase 13' },
  { id: '7', title: 'MT4 bridge for prop firm autonomous execution', status: 'idea', agent: 'ghost', domain: 'Trading · Phase 14', note: 'Ghost flagged' },
  { id: '8', title: 'Trading pattern agent — autonomous prop firm rotation', status: 'idea', agent: 'sparticus', domain: 'Trading · Phase 14' },
  { id: '9', title: 'Business strategy tool — SaaS frontend', status: 'idea', agent: 'joint', domain: 'Business · Future' },
  { id: '10', title: 'Arthur strategy document — self-enforcing prompts', status: 'idea', agent: 'sparticus', domain: 'Trading' },
  { id: '11', title: 'Encrypted silo sync for full VPS memory', status: 'idea', agent: 'unassigned', domain: 'Phase 13+' },
  { id: '12', title: 'CODEX review pulse — after CLAUDE_CODE sessions', status: 'idea', agent: 'ghost', domain: 'Code domain' },
  { id: '13', title: 'Arthur Pine Script — self-enforcing strategy doc', status: 'idea', agent: 'sparticus', domain: 'Trading' },
  { id: '14', title: 'CODE domain build — Sparticus, Ghost, Joint agents', status: 'done', agent: 'joint', domain: 'Core', timestamp: '2026-05-01' },
  { id: '15', title: 'Spencer Drive fix — Commitment.docx read working', status: 'done', agent: 'sparticus', domain: 'Personal', timestamp: '2026-05-01' },
  { id: '16', title: 'Smart memory — threshold removed, ranked retrieval', status: 'done', agent: 'sparticus', domain: 'Core', timestamp: '2026-05-01' },
  { id: '17', title: 'Trust hierarchy — document HIGHEST, API HIGH, Qwen MEDIUM', status: 'done', agent: 'sparticus', domain: 'Core', timestamp: '2026-05-01' },
  { id: '18', title: 'VPS deployment 4 — smart memory + Spencer fixes', status: 'done', agent: 'joint', domain: 'Infrastructure', timestamp: '2026-05-01' },
];

export default function CodePage({ authToken, onBack, initialPreview }: CodePageProps) {
  const [tasks, setTasks] = useState<TaskEntry[]>(DEMO_TASKS);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'in_progress' | 'queued' | 'idea' | 'done' | 'agent' | 'domain'>('all');
  const [authenticated, setAuthenticated] = useState(false);
  const [authInput, setAuthInput] = useState('');

  // Sparticus and Ghost authenticate using passphrase 'ghost' when making changes
  // User authenticates manually via the passphrase input in the workspace header
  const [authError, setAuthError] = useState(false);
  const [sparticusLast] = useState('01/05 · Claude Sonnet');
  const [ghostLast] = useState('01/05 · GPT-5.5');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskStatus, setNewTaskStatus] = useState<'idea' | 'queued' | 'in_progress'>('idea');
  const [newTaskAgent, setNewTaskAgent] = useState<'unassigned' | 'sparticus' | 'ghost' | 'joint'>('unassigned');
  const [saving, setSaving] = useState(false);

  // ── CONFIRMATION GATE ─────────────────────────────────────────────────────
  // Agent proposals live here — never applied to tasks until user confirms.
  // Joint proposals require BOTH Sparticus and Ghost to sign off first,
  // then user confirms. Sparticus/Ghost can only propose on their own tasks.
  const [proposals, setProposals] = useState<TaskProposal[]>([]);
  const [proposalsLoaded, setProposalsLoaded] = useState(false);

  // ── SESSIONS FEED (Fix 5) ─────────────────────────────────────────────────
  // The VISIBLE wrap artifact: one plain-English line per coder session, served
  // by GET /api/code/wraps (whitelisted — the internal build summary is durable
  // in wrap_summaries.json but never rendered as dashboard copy). This is the
  // artifact the single per-wrap activity event points at.
  // Owner ruling: the ONE dashboard-update stream — every CODE board change (wrap,
  // task, proposal) as a timestamped plain-English row. UNCAPPED (no engineering
  // for scale). A wrap renders as "wrap tag: <coder>"; other kinds carry their own
  // plain tag. Same rows Rexy's pulse selects from by event time.
  interface WrapSession { who: string; via: string; about: string; when: string; kind: string; }
  const [wraps, setWraps] = useState<WrapSession[]>([]);

  // Auth handled globally by axios interceptor in App.tsx — no manual headers needed

  // ── LIVE PREVIEW PANE ─────────────────────────────────────────────────────
  // Initialised from App-level prop (set when ◈ preview clicked in any chat).
  // Also listens for subsequent rexy-preview events while CodePage is mounted.
  const [previewCode, setPreviewCode] = useState<string | null>(initialPreview?.code ?? null);
  const [previewFilename, setPreviewFilename] = useState<string>(initialPreview?.filename ?? '');
  const [previewLang, setPreviewLang] = useState<string>(initialPreview?.lang ?? '');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { code, filename, lang } = (e as CustomEvent).detail;
      setPreviewCode(code);
      setPreviewFilename(filename || 'preview');
      setPreviewLang(lang || 'tsx');
    };
    window.addEventListener('rexy-preview', handler);
    return () => window.removeEventListener('rexy-preview', handler);
  }, []);

  // Build the sandboxed iframe content from the code
  // TSX/JSX: wraps in a minimal React + ReactDOM CDN page
  // HTML: renders directly
  const buildPreviewHtml = useCallback((code: string, lang: string): string => {
    if (lang === 'html') {
      return `<!DOCTYPE html><html><head>
        <meta charset="utf-8"/>
        <style>body{margin:0;padding:16px;background:#0f0f12;color:#e8e8ec;font-family:system-ui,sans-serif;}*{box-sizing:border-box;}</style>
      </head><body>${code}</body></html>`;
    }

    // Extract inline styles from the code to approximate the visual appearance
    // Pull out style objects and convert to actual CSS for static rendering
    const extractStyles = (src: string): Record<string, string> => {
      const styles: Record<string, string> = {};
      const styleRe = /style=\{\{([^}]+(?:\}[^}]+)*)\}\}/g;
      let styleMatch = styleRe.exec(src);
      while (styleMatch !== null) {
        const props = styleMatch[1];
        const bgMatch = props.match(/backgroundColor:\s*['"]([^'"]+)['"]/);
        const colorMatch = props.match(/color:\s*['"]([^'"]+)['"]/);
        const borderMatch = props.match(/borderRadius:\s*['"]([^'"]+)['"]/);
        if (bgMatch) styles.backgroundColor = bgMatch[1];
        if (colorMatch) styles.color = colorMatch[1];
        if (borderMatch) styles.borderRadius = borderMatch[1];
        styleMatch = styleRe.exec(src);
      }
      return styles;
    };

    const extractTextContent = (src: string): string[] => {
      const texts: string[] = [];
      const textRe = />\s*([A-Z][^<{}\n]+?)\s*</g;
      let tm = textRe.exec(src);
      while (tm !== null) {
        const t = tm[1].trim();
        if (t && !t.includes('=>') && !t.includes('const ') && t.length < 80) {
          texts.push(t);
        }
        tm = textRe.exec(src);
      }
      const seen: Record<string, boolean> = {};
      return texts.filter(t => { if (seen[t]) return false; seen[t] = true; return true; }).slice(0, 10);
    };

    // Detect component type from code patterns
    const isCounter = /count|Count|counter|Counter/.test(code) && /button/.test(code);
    const isCard = /card|Card/.test(code) && /title|Title/.test(code);
    const isForm = /input|Input|form|Form|label|Label/.test(code);
    const isButton = /button|Button/.test(code) && code.split('button').length <= 4;

    const texts = extractTextContent(code);

    // Build a faithful static approximation based on component type
    let bodyHtml = '';

    if (isCounter) {
      bodyHtml = `
        <div style="display:flex;flex-direction:column;align-items:center;gap:16px;padding:32px;
          background:#141414;border:1px solid #2a2a2a;border-radius:12px;width:fit-content;margin:32px auto;">
          <h2 style="margin:0;color:#e8e8e8;font-size:18px;">Counter</h2>
          <div style="font-size:48px;font-weight:bold;color:#e8e8e8;min-width:80px;text-align:center;">0</div>
          <div style="display:flex;gap:12px;">
            <button style="width:48px;height:48px;font-size:24px;background:#1a1a1a;color:#e8e8e8;
              border:1px solid #2a2a2a;border-radius:8px;cursor:pointer;">−</button>
            <button style="width:48px;height:48px;font-size:24px;background:#1a1a1a;color:#e8e8e8;
              border:1px solid #2a2a2a;border-radius:8px;cursor:pointer;">+</button>
          </div>
          <button style="padding:6px 16px;font-size:13px;background:transparent;color:#666;
            border:1px solid #2a2a2a;border-radius:6px;cursor:pointer;">Reset</button>
        </div>`;
    } else if (isCard) {
      const title = texts.find(t => t.length > 2 && t.length < 40) || 'Card Title';
      const subtitle = texts.find(t => t !== title) || 'Subtitle text goes here';
      bodyHtml = `
        <div style="padding:20px 24px;background:#141414;border:1px solid #2a2a2a;
          border-radius:8px;width:320px;margin:32px auto;">
          <h3 style="margin:0;font-size:16px;font-weight:600;color:#e8e8e8;">${title}</h3>
          <p style="margin:4px 0 0;font-size:13px;color:#666;line-height:1.4;">${subtitle}</p>
        </div>`;
    } else if (isForm) {
      bodyHtml = `
        <form style="display:flex;flex-direction:column;gap:12px;padding:24px;
          background:#141414;border:1px solid #2a2a2a;border-radius:8px;width:320px;margin:32px auto;"
          onsubmit="return false">
          ${texts.filter(t => t.length < 30).map(t =>
            `<div>
              <label style="font-size:12px;color:#888;display:block;margin-bottom:4px;">${t}</label>
              <input style="width:100%;padding:8px 10px;background:#1a1a1a;border:1px solid #2a2a2a;
                border-radius:6px;color:#e8e8e8;font-size:13px;outline:none;" placeholder="${t}..."/>
            </div>`
          ).join('')}
          <button type="submit" style="padding:8px 16px;background:#7F77DD;color:#fff;
            border:none;border-radius:6px;cursor:pointer;font-size:13px;">Submit</button>
        </form>`;
    } else if (isButton) {
      const label = texts[0] || 'Button';
      bodyHtml = `
        <div style="padding:32px;display:flex;gap:12px;flex-wrap:wrap;justify-content:center;">
          <button style="padding:8px 20px;background:#7F77DD;color:#fff;border:none;
            border-radius:6px;cursor:pointer;font-size:14px;">${label}</button>
        </div>`;
    } else {
      // Generic — show extracted text content in a component-like container
      bodyHtml = `
        <div style="padding:20px;background:#141414;border:1px solid #2a2a2a;
          border-radius:8px;margin:32px auto;max-width:480px;">
          ${texts.length > 0
            ? texts.map(t => `<p style="margin:0 0 8px;color:#e8e8e8;font-size:14px;">${t}</p>`).join('')
            : '<p style="color:#555;font-size:13px;">Component preview — visual approximation</p>'
          }
        </div>`;
    }

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #0f0f12; color: #e8e8ec;
           font-family: system-ui, -apple-system, sans-serif; min-height: 100vh; }
    button:hover { opacity: 0.85; }
    input:focus { border-color: #7F77DD !important; }
  </style>
</head>
<body>
  <div style="padding:8px;text-align:center;font-size:9px;color:#333;
    text-transform:uppercase;letter-spacing:0.1em;border-bottom:0.5px solid #1a1a1f;margin-bottom:0;">
    Visual approximation — not live execution
  </div>
  ${bodyHtml}
</body>
</html>`;
  }, []);

  // When previewCode changes inject into iframe via srcdoc — no blob URLs needed
  useEffect(() => {
    if (!previewCode || !iframeRef.current) return;
    const html = buildPreviewHtml(previewCode, previewLang);
    iframeRef.current.srcdoc = html;
  }, [previewCode, previewLang, buildPreviewHtml]);

  // Load tasks from server on mount
  useEffect(() => {
    async function fetchTasks() {
      try {
        const res = await axios.get(`${API_BASE}/code/tasks`);
        if (res.data.success && res.data.tasks.length > 0) {
          setTasks(res.data.tasks);
        }
      } catch (e) {
        console.error('Failed to load tasks:', e);
      } finally {
        setTasksLoaded(true);
      }
    }
    fetchTasks();
  }, []);

  useEffect(() => {
    let active = true;
    let interval: ReturnType<typeof setInterval>;

    async function fetchProposals() {
      if (!active) return;
      try {
        const res = await axios.get(`${API_BASE}/code/proposals`);
        if (res.data.success) {
          const raw: any[] = res.data.proposals || [];
          setProposals(
            raw
              // Fix 5 (fourth pass): one wrap = ONE visible dashboard artifact.
              // A wrap-born proposal (carries wrap_summary) is represented by its
              // Recent Sessions entry — it must not ALSO surface as a pending card.
              .filter(p => (p.status ?? 'pending_review') !== 'rejected' && !p.wrap_summary)
              .map(p => ({
                ...p,
                agent:     p.agent ?? p.proposedBy,
                // Fix 5 (third pass): ONLY the plain-English `about` line renders as
                // dashboard copy. The raw message/description fallback is REMOVED —
                // internal text is durable in the store but never rendered; a legacy
                // entry without `about` shows no body (never the jargon wall).
                message:   p.about ?? '',
                timestamp: p.timestamp ?? p.created_at?.slice(0, 10) ?? '',
                type:      VALID_PROPOSAL_TYPES.has(p.type) ? p.type : 'status_change',
              }))
          );
        }
      } catch (e) {
        // Stop polling on network failure — main.js CORS fix must be in place
        clearInterval(interval);
        active = false;
      } finally {
        setProposalsLoaded(true);
      }
    }

    fetchProposals();
    interval = setInterval(fetchProposals, 5000);
    return () => { clearInterval(interval); active = false; };
  }, []);

  // Sessions feed — same auth idiom (global axios interceptor); 30s poll.
  useEffect(() => {
    let active = true;
    async function fetchWraps() {
      if (!active) return;
      try {
        const res = await axios.get(`${API_BASE}/code/wraps`);
        if (res.data.success) {
          // Plain-English entries only — legacy wraps without an `about` line are
          // not dashboard copy (their internal text stays durable, never rendered).
          setWraps((res.data.wraps || []).filter((w: WrapSession) => w.about));
        }
      } catch (e) { /* graceful — feed hides when unavailable */ }
    }
    fetchWraps();
    const wi = setInterval(fetchWraps, 30000);
    return () => { clearInterval(wi); active = false; };
  }, []);

  // Accept a proposal — applies the change to tasks, removes from proposals
  async function acceptProposal(proposal: TaskProposal) {
    if (!proposal.proposedStatus) {
      // ACP build/review proposals carry no proposedStatus — guard exits
      // before any task mutation.
      setProposals(prev => prev.filter(p => p.id !== proposal.id));
      try { await axios.delete(`${API_BASE}/code/proposals/${proposal.id}`); } catch (e) { /* graceful */ }
      return;
    }
    if (proposal.type === 'new_task' || (proposal.type === 'mark_done' && !proposal.taskId)) {
      // New task from agent
      const newTask: TaskEntry = {
        id: Date.now().toString(),
        title: proposal.taskTitle || tasks.find(t => t.id === proposal.taskId)?.title || `Task ${proposal.taskId ?? '—'}`,
        status: proposal.proposedStatus,
        agent: (proposal.proposedAgent ?? 'unassigned') as TaskEntry['agent'],
        domain: proposal.proposedDomain,
        timestamp: new Date().toISOString().slice(0, 10),
      };
      const updated = [newTask, ...tasks];
      setTasks(updated);
      await saveTasks(updated);
    } else if (proposal.taskId) {
      // Status change on existing task
      await updateTaskStatus(proposal.taskId, proposal.proposedStatus);
    }
    // Remove proposal from pending queue
    const remaining = proposals.filter(p => p.id !== proposal.id);
    setProposals(remaining);
    try {
      await axios.delete(`${API_BASE}/code/proposals/${proposal.id}`);
    } catch (e) { /* graceful */ }
  }

  // Reject a proposal — removes without applying
  async function rejectProposal(proposalId: string) {
    setProposals(prev => prev.filter(p => p.id !== proposalId));
    try {
      await axios.delete(`${API_BASE}/code/proposals/${proposalId}`);
    } catch (e) { /* graceful */ }
  }

  async function saveTasks(updated: TaskEntry[]) {
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/code/tasks`, { tasks: updated });
      console.log('[CodePage] Tasks saved OK —', res.data);
    } catch (e: any) {
      console.error('[CodePage] Save failed:', e?.response?.status, e?.response?.data || e?.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateTaskStatus(taskId: string, newStatus: string) {
    const updated = tasks.map(t => t.id === taskId ? {
      ...t,
      status: newStatus as TaskEntry['status'],
      timestamp: newStatus === 'done' ? new Date().toISOString().slice(0, 10) : t.timestamp,
    } : t);
    setTasks(updated);
    await saveTasks(updated);
  }

  async function addTask() {
    if (!newTaskTitle.trim()) return;
    const newTask: TaskEntry = {
      id: Date.now().toString(),
      title: newTaskTitle.trim(),
      status: newTaskStatus,
      agent: newTaskAgent,
      timestamp: new Date().toISOString().slice(0, 10),
    };
    const updated = [newTask, ...tasks];
    setTasks(updated);
    setNewTaskTitle('');
    await saveTasks(updated);
  }

  const counts = {
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    queued: tasks.filter(t => t.status === 'queued').length,
    idea: tasks.filter(t => t.status === 'idea').length,
    done: tasks.filter(t => t.status === 'done').length,
  };

  const ghostReviewPending = tasks.filter(t => t.ghost_review === 'pending').length;
  const sparticusTasks = tasks.filter(t => t.agent === 'sparticus' && t.status !== 'done').length;

  function handleAuth() {
    if (authInput.trim().toLowerCase() === 'ghost') {
      setAuthenticated(true);
      setAuthError(false);
    } else {
      setAuthError(true);
    }
    setAuthInput('');
  }

  const filtered = activeTab === 'all' ? tasks
    : tasks.filter(t => t.status === activeTab);

  const sections = activeTab === 'all'
    ? ['in_progress', 'queued', 'idea', 'done'] as const
    : [activeTab] as any[];

  const sectionLabel: Record<string, string> = {
    in_progress: 'In progress',
    queued: 'Queued',
    idea: 'Ideas',
    done: 'Recently done',
  };

  const borderColor: Record<string, string> = {
    in_progress: '#7F77DD',
    queued: 'var(--color-border-secondary)',
    idea: '#1D9E75',
    done: 'var(--color-border-tertiary)',
  };

  const outer: React.CSSProperties = {
    background: '#1a1a1f',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-sans)',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    overflowY: 'auto',
  };

  const card: React.CSSProperties = {
    background: '#22222a',
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: 'var(--border-radius-lg)',
    overflow: 'hidden',
  };

  const metricCard: React.CSSProperties = {
    background: '#2a2a34',
    borderRadius: 'var(--border-radius-md)',
    padding: '10px 14px',
    textAlign: 'center',
  };

  const taskCardBase: React.CSSProperties = {
    background: '#2a2a34',
    border: '0.5px solid rgba(255,255,255,0.08)',
    borderRadius: 'var(--border-radius-md)',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 6,
  };

  // Fix 5 (owner bar: nothing renders blank) — DISPLAY-ONLY filter, data untouched,
  // pending the owner's ruling on what proposals are. Only rows that are (a) genuinely
  // awaiting confirmation (the UI's only real pending statuses — 'signed'/
  // 'joint_signed'/'superseded_*'/'implemented_*' are CLOSED workflow paper the old
  // filter wrongly showed as pending) and (b) have something meaningful to show
  // (a plain `about` or a resolvable task title) reach the board. An entry with
  // nothing meaningful to say is not on the board at all — never a blank "Task —".
  const visibleProposals = proposals.filter(p => {
    const status = (p as any).status ?? 'pending_review';
    if (status !== 'pending_review' && status !== 'pending') return false;
    return Boolean((p as any).about || p.taskTitle || tasks.find(t => t.id === p.taskId)?.title);
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#1a1a1f' }}>

      {/* ── MAIN WORKSPACE ───────────────────────────────────────────────── */}
      <div style={{ ...outer, flex: previewCode ? '0 0 55%' : '1', minWidth: 0 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && (
              <button onClick={onBack} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-sans)', marginRight: 4 }}>← Back</button>
            )}
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7F77DD', display: 'inline-block' }} />
            CODE — Workspace
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Sparticus · Ghost · Joint · Shared memory
          </div>
        </div>
        {!authenticated ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={authInput}
              onChange={e => setAuthInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAuth()}
              placeholder="Passphrase..."
              type="password"
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 'var(--border-radius-md)', border: `0.5px solid ${authError ? '#ef4444' : 'rgba(255,255,255,0.15)'}`, background: 'transparent', color: 'var(--color-text-primary)', fontFamily: 'var(--font-sans)', width: 130 }}
            />
            <button onClick={handleAuth} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
              🔒 Authenticate
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#1D9E7522', color: '#5DCAA5', border: '0.5px solid #1D9E7544' }}>
            ✓ Authenticated
          </span>
        )}
      </div>

      {/* Agent status row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ ...card, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <AgentDot agent="sparticus" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Sparticus</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            Last active: {sparticusLast}<br />
            {sparticusTasks} tasks active · {ghostReviewPending} awaiting Ghost review
          </div>
        </div>
        <div style={{ ...card, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <AgentDot agent="ghost" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Ghost</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            Last review: {ghostLast}<br />
            {ghostReviewPending} flags pending · {counts.done} reviews completed
          </div>
        </div>
      </div>

      {/* ── SESSIONS FEED (Fix 5) — the visible wrap artifact ────────────── */}
      {wraps.length > 0 && (
        <div style={{ ...card, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#AFA9EC', marginBottom: 8 }}>
            Recent updates
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {wraps.map((w, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                    {w.kind === 'wrap' ? 'wrap tag:' : w.kind === 'task' ? 'task update:' : 'proposal:'}
                  </span>
                  {w.who && <AgentBadge agent={w.who} />}
                  {w.via && w.via !== 'direct' && (
                    <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>via {w.via}</span>
                  )}
                  {w.when && (
                    <span style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
                      {w.when.slice(0, 16).replace('T', ' · ')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--color-text-primary)' }}>{w.about}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PENDING PROPOSALS BANNER ─────────────────────────────────────── */}
      {tasksLoaded && visibleProposals.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#AFA9EC' }}>
            {visibleProposals.length} pending proposal{visibleProposals.length > 1 ? 's' : ''} — awaiting confirmation
          </div>
          {visibleProposals.map(p => {
            // Fix 5 (fourth pass): titles come from the task registry or the FULL
            // plain-English `about` (no 70-char slice — no silent truncation
            // anywhere on the about path); otherwise a neutral placeholder. No raw
            // internal text renders, including legacy entries.
            const resolvedTitle =
              p.taskTitle
              || tasks.find(t => t.id === p.taskId)?.title
              || ((p as any).about ? String((p as any).about) : `Task ${p.taskId ?? '—'}`);

            const typeStyle: Record<string, { border: string; bg: string; outline: string }> = {
              new_task:      { border: '#1D9E75', bg: 'rgba(29,158,117,0.06)',  outline: 'rgba(29,158,117,0.30)' },
              mark_done:     { border: '#5DA855', bg: 'rgba(93,168,85,0.06)',   outline: 'rgba(93,168,85,0.30)'  },
              status_change: { border: '#7F77DD', bg: 'rgba(127,119,221,0.06)', outline: 'rgba(127,119,221,0.30)' },
            };
            const ts = typeStyle[p.type] ?? typeStyle.status_change;

            return (
              <div key={p.id} style={{
                background: ts.bg,
                border: `0.5px solid ${ts.outline}`,
                borderLeft: `3px solid ${ts.border}`,
                borderRadius: 'var(--border-radius-md)',
                padding: '10px 14px',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <AgentBadge agent={p.agent} />
                    <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                      {p.type === 'new_task' ? 'proposes adding' :
                       p.type === 'mark_done' ? 'proposes marking done' : 'proposes status change'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{p.timestamp}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{resolvedTitle}</div>
                  {p.proposedStatus && (
                    <div style={{ fontSize: 11, color: '#AFA9EC', marginBottom: 4 }}>
                      → {p.proposedStatus.replace('_', ' ')}
                      {p.proposedDomain ? ` · ${p.proposedDomain}` : ''}
                    </div>
                  )}
                  {p.message && (
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
                      "{p.message}"
                    </div>
                  )}
                  {p.agent === 'joint' && p.jointSignoff && (
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4, display: 'flex', gap: 8 }}>
                      <span style={{ color: p.jointSignoff.sparticus ? '#1D9E75' : '#E05252' }}>
                        {p.jointSignoff.sparticus ? '✓' : '○'} Sparticus
                      </span>
                      <span style={{ color: p.jointSignoff.ghost ? '#1D9E75' : '#E05252' }}>
                        {p.jointSignoff.ghost ? '✓' : '○'} Ghost
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexDirection: 'column' }}>
                  <button
                    onClick={() => acceptProposal(p)}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(29,158,117,0.4)', background: 'rgba(29,158,117,0.1)', color: '#5DCAA5', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 500 }}
                  >
                    ✓ Confirm
                  </button>
                  <button
                    onClick={() => rejectProposal(p.id)}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(224,82,82,0.3)', background: 'rgba(224,82,82,0.07)', color: '#E05252', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
                  >
                    ✕ Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Metric row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { label: 'In progress', value: counts.in_progress, color: '#7F77DD' },
          { label: 'Queued', value: counts.queued, color: 'var(--color-text-primary)' },
          { label: 'Ideas', value: counts.idea, color: '#1D9E75' },
          { label: 'Done', value: counts.done, color: 'var(--color-text-secondary)' },
        ].map(m => (
          <div key={m.label} style={metricCard}>
            <div style={{ fontSize: 22, fontWeight: 500, color: m.color }}>{m.value}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '0.5px solid rgba(255,255,255,0.08)', paddingBottom: 0 }}>
        {(['all', 'in_progress', 'queued', 'idea', 'done'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              fontSize: 12, padding: '5px 14px',
              borderRadius: 'var(--border-radius-md) var(--border-radius-md) 0 0',
              border: '0.5px solid transparent',
              cursor: 'pointer', fontFamily: 'var(--font-sans)',
              background: activeTab === tab ? '#2a2a34' : 'transparent',
              color: activeTab === tab ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              fontWeight: activeTab === tab ? 500 : 400,
              borderColor: activeTab === tab ? 'rgba(255,255,255,0.08)' : 'transparent',
            }}
          >
            {tab === 'in_progress' ? 'In progress' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Pipeline */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sections.map(section => {
          const sectionTasks = tasks.filter(t => t.status === section);
          if (sectionTasks.length === 0) return null;
          return (
            <div key={section} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary)', marginBottom: 8, marginTop: 4 }}>
                {sectionLabel[section]}
              </div>
              {sectionTasks.map(task => (
                <div
                  key={task.id}
                  style={{
                    ...taskCardBase,
                    borderLeft: `2.5px solid ${borderColor[section]}`,
                    opacity: section === 'done' ? 0.55 : 1,
                  }}
                >
                  <StatusIcon status={task.status} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{task.title}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {task.agent !== 'unassigned' && <AgentBadge agent={task.agent} />}
                      {task.ghost_review === 'pending' && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#FCEBEB', color: '#791F1F', fontWeight: 500 }}>
                          Ghost review pending
                        </span>
                      )}
                      {task.note && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#E1F5EE', color: '#085041', fontWeight: 500 }}>
                          {task.note}
                        </span>
                      )}
                      {task.status === 'done' && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#EAF3DE', color: '#27500A', fontWeight: 500 }}>
                          Done
                        </span>
                      )}
                      {task.domain && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          {task.timestamp ? `${task.timestamp} · ` : ''}{task.domain}
                        </span>
                      )}
                    </div>
                  </div>
                  {authenticated && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                      <select
                        value={task.status}
                        onChange={e => {
                          const newStatus = e.target.value as TaskEntry['status'];
                          // Update local state immediately for visual feedback
                          setTasks(prev => prev.map(t =>
                            t.id === task.id ? { ...t, status: newStatus } : t
                          ));
                        }}
                        style={{ fontSize: 10, padding: '2px 6px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.1)', background: '#1a1a1f', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)' }}
                      >
                        <option value="in_progress">in progress</option>
                        <option value="queued">queued</option>
                        <option value="idea">idea</option>
                        <option value="done">done</option>
                      </select>
                      <button
                        onClick={() => updateTaskStatus(task.id, task.status)}
                        style={{
                          fontSize: 9, padding: '2px 8px',
                          borderRadius: 'var(--border-radius-md)',
                          border: '0.5px solid rgba(127,119,221,0.4)',
                          background: 'rgba(127,119,221,0.1)',
                          color: '#AFA9EC', cursor: 'pointer',
                          fontFamily: 'var(--font-sans)',
                        }}
                      >
                        {saving ? 'saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Add idea — only when authenticated */}
      {authenticated && (
        <div style={{ ...card, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
            Add idea or task {saving && <span style={{ color: '#7F77DD', fontSize: 10 }}>saving...</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={newTaskTitle}
              onChange={e => setNewTaskTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTask()}
              placeholder="Describe the task or idea..."
              style={{ flex: 1, fontSize: 12, padding: '6px 10px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--color-text-primary)', fontFamily: 'var(--font-sans)' }}
            />
            <select
              value={newTaskStatus}
              onChange={e => setNewTaskStatus(e.target.value as any)}
              style={{ fontSize: 12, padding: '6px 10px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.15)', background: '#1a1a1f', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)' }}
            >
              <option value="idea">idea</option>
              <option value="queued">queued</option>
              <option value="in_progress">in progress</option>
            </select>
            <select
              value={newTaskAgent}
              onChange={e => setNewTaskAgent(e.target.value as any)}
              style={{ fontSize: 12, padding: '6px 10px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(255,255,255,0.15)', background: '#1a1a1f', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)' }}
            >
              <option value="unassigned">unassigned</option>
              <option value="sparticus">sparticus</option>
              <option value="ghost">ghost</option>
              <option value="joint">joint</option>
            </select>
            <button
              onClick={addTask}
              disabled={!newTaskTitle.trim()}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid rgba(127,119,221,0.4)', background: 'rgba(127,119,221,0.1)', color: '#AFA9EC', cursor: 'pointer', fontFamily: 'var(--font-sans)', opacity: newTaskTitle.trim() ? 1 : 0.4 }}
            >
              Add
            </button>
          </div>
        </div>
      )}

      </div>{/* end main workspace */}

      {/* ── LIVE PREVIEW PANE ──────────────────────────────────────────────── */}
      {previewCode && (
        <div style={{
          flex: '0 0 45%',
          borderLeft: '0.5px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          background: '#111',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
        }}>
          {/* Preview header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '0.5px solid rgba(255,255,255,0.08)',
            background: '#1a1a1f',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#7F77DD', display: 'inline-block' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Live Preview
              </span>
              <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                {previewFilename}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                sandboxed · isolated
              </span>
              <button
                onClick={() => setPreviewCode(null)}
                style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 'var(--border-radius-md)',
                  border: '0.5px solid rgba(255,255,255,0.1)',
                  background: 'transparent', color: 'var(--color-text-secondary)',
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>

          {/* Sandboxed iframe — completely isolated from Rexy */}
          <iframe
            ref={iframeRef}
            title="Rexy Live Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{
              flex: 1,
              border: 'none',
              width: '100%',
              background: '#0f0f12',
            }}
          />

          {/* Preview footer — Ghost review prompt */}
          <div style={{
            padding: '8px 14px',
            borderTop: '0.5px solid rgba(255,255,255,0.06)',
            background: '#1a1a1f',
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
              Ghost reviews code + visual output together
            </span>
            <span style={{ fontSize: 9, color: '#AFA9EC', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
              ◈ Sparticus Preview
            </span>
          </div>
        </div>
      )}

    </div>
  );
}
